import async from 'async';
import { ModGroupUtils, } from '@biteinc/db-models-interface';
import { ItemHiddenState, MenuItemSaleUnit, QuickAddEligibility } from '@biteinc/enums';
export const menuItemOrModProps = [
    '_id',
    'name',
    'posName',
    'isMod',
    'priceOptions',
];
export const modGroupProps = [
    '_id',
    'name',
    'posName',
    'items',
    'i9nItems',
    'hideFromMenu',
    'minSelectable',
    'posMinSelectableOverride',
    'minAggregateQuantity',
    'posMinAggregateQuantityOverride',
];
/**
 * A price option the guest must not be auto-selected into. Covers `ShownButUnavailable` (86'd but
 * kept in the payload so the picker still shows it, greyed out) and the two hidden states.
 */
function priceOptionIsUnavailable(priceOption) {
    return (priceOption.hiddenState ?? ItemHiddenState.Shown) !== ItemHiddenState.Shown;
}
/**
 * Returns the default or only price option for an item.
 * Used to determine which price option to check for quick-add eligibility.
 *
 * An unavailable price option is never returned: neither an 86'd `selectedByDefault` option nor a
 * lone 86'd option may be auto-selected. This intentionally does NOT fall back to "the sole
 * available option out of many" — with several options where all but one are 86'd, returning
 * `undefined` keeps quick-add off and sends the guest into the item view, where the picker shows
 * which constituent survives.
 */
export function maybeGetDefaultOrOnlyPriceOption(item) {
    const defaultPriceOption = item.priceOptions?.find((po) => po.selectedByDefault);
    if (defaultPriceOption) {
        return priceOptionIsUnavailable(defaultPriceOption) ? undefined : defaultPriceOption;
    }
    if (item.priceOptions?.length === 1) {
        const onlyPriceOption = item.priceOptions[0];
        return priceOptionIsUnavailable(onlyPriceOption) ? undefined : onlyPriceOption;
    }
    return undefined;
}
/**
 * Checks if a mod group has any constraints (min or max).
 */
export function modGroupHasConstraints(modGroup) {
    return !!(modGroup.minSelectable ||
        modGroup.maxSelectable ||
        modGroup.minAggregateQuantity ||
        modGroup.maxAggregateQuantity ||
        modGroup.minChoiceQuantity ||
        modGroup.maxChoiceQuantity);
}
/**
 * Checks if a mod group has maximum constraints.
 */
export function modGroupHasMaxConstraints(modGroup) {
    return !!(modGroup.maxSelectable || modGroup.maxAggregateQuantity || modGroup.maxChoiceQuantity);
}
/**
 * Checks if a mod group has minimum constraints.
 */
export function modGroupHasMinConstraints(modGroup) {
    return !!(modGroup.minSelectable || modGroup.minAggregateQuantity || modGroup.minChoiceQuantity);
}
/**
 * Core algorithm for determining if an item can be quick-added.
 * This is the single source of truth for the quick-add eligibility logic.
 *
 * @param accessors - Functions to access data in a format-agnostic way
 * @param eligibility - The quick-add eligibility setting from site settings
 * @param item - The item to check
 * @returns true if the item can be quick-added, false otherwise
 */
export function canQuickAddItemCore(accessors, eligibility, item) {
    const priceOption = maybeGetDefaultOrOnlyPriceOption(item);
    if (priceOption?.saleUnit !== undefined && priceOption.saleUnit !== MenuItemSaleUnit.Each) {
        return false;
    }
    const modGroupIds = priceOption ? accessors.getModGroupIds(priceOption) : undefined;
    switch (eligibility) {
        // this is the simplest case, where the item has no mod groups
        case QuickAddEligibility.HasNoModGroups: {
            return !!priceOption && !modGroupIds?.length;
        }
        // this is the case where the item has mod groups, but none of them have constraints like
        // min/max selectable, min/max aggregate quantity, etc.
        case QuickAddEligibility.HasModGroupsNoRequiredChoices: {
            return (!!priceOption &&
                (!modGroupIds?.length ||
                    modGroupIds.every((modGroupId) => {
                        const modGroup = accessors.getModGroup(modGroupId);
                        if (!modGroup) {
                            return true;
                        }
                        if (modGroupHasMinConstraints(modGroup)) {
                            return false;
                        }
                        const selectedByDefault = modGroup.items.filter((mod) => {
                            return !!mod.selectedByDefaultQuantity;
                        });
                        return (selectedByDefault.length === 0 ||
                            selectedByDefault.every((itemRef) => {
                                const mod = accessors.getModFromItemRef(itemRef);
                                return canQuickAddItemCore(accessors, eligibility, mod);
                            }));
                    })));
        }
        // this is the case where the item has mod groups, they have constraints
        // but they are satisfied by the default selection
        case QuickAddEligibility.AllRequiredModGroupsArePreselected: {
            return (!!priceOption &&
                (!modGroupIds?.length ||
                    modGroupIds.every((modGroupId) => {
                        const modGroup = accessors.getModGroup(modGroupId);
                        if (!modGroup) {
                            return true;
                        }
                        const selectedByDefault = modGroup.items.filter((mod) => {
                            return !!mod.selectedByDefaultQuantity;
                        });
                        const selectedByDefaultAmount = selectedByDefault.reduce((acc, itemRef) => {
                            return acc + (itemRef.selectedByDefaultQuantity || 0);
                        }, 0);
                        return (selectedByDefaultAmount >= (modGroup.minSelectable || 0) &&
                            selectedByDefaultAmount >= (modGroup.minAggregateQuantity || 0) &&
                            (selectedByDefault.length === 0 ||
                                selectedByDefault.every((itemRef) => {
                                    const mod = accessors.getModFromItemRef(itemRef);
                                    return canQuickAddItemCore(accessors, eligibility, mod);
                                })));
                    })));
        }
        default: {
            // this handles undefined eligibility as well as any unknown values
            return false;
        }
    }
}
export async function checkQuickAddEligibility(menuItemOrMod, getModGroups, getMods) {
    const itemName = `${menuItemOrMod.posName ?? menuItemOrMod.name} (${menuItemOrMod._id.toString()})`;
    const defaultPriceOptions = menuItemOrMod.priceOptions.filter((priceOption) => {
        return priceOption.selectedByDefault ?? !!priceOption.i9nSelectedByDefault;
    });
    if (defaultPriceOptions.length > 1) {
        return { itemIsQuickAddable: false, reason: `${itemName}: more than one default price option` };
    }
    if (!defaultPriceOptions.length && menuItemOrMod.priceOptions.length > 1) {
        return { itemIsQuickAddable: false, reason: `${itemName}: more than one price option` };
    }
    const priceOption = defaultPriceOptions.length
        ? defaultPriceOptions[0]
        : menuItemOrMod.priceOptions[0];
    if (!priceOption.addonSetIds?.length) {
        return { itemIsQuickAddable: true };
    }
    const modGroups = await getModGroups(priceOption.addonSetIds);
    let modGroupReason = null;
    await async.findSeries(modGroups, async (modGroup) => {
        const modGroupName = `${modGroup.posName ?? modGroup.name} (${modGroup._id.toString()})`;
        // Hidden so not required
        if (modGroup.hideFromMenu) {
            return false;
        }
        // No min requirements
        const minSelectable = modGroup.posMinSelectableOverride || modGroup.minSelectable || 0;
        const minAggregateQuantity = modGroup.posMinAggregateQuantityOverride || modGroup.minAggregateQuantity || 0;
        if (!minSelectable && !minAggregateQuantity) {
            return false;
        }
        const selectedByDefaultModRefs = modGroup.items.filter((modRef) => {
            return ModGroupUtils.modIsSelectedByDefault(modGroup, modRef._id);
        });
        const selectedByDefaultQuantity = selectedByDefaultModRefs.reduce((acc, modRef) => {
            return acc + ModGroupUtils.getModSelectedByDefaultQuantity(modGroup, modRef._id);
        }, 0);
        if (selectedByDefaultModRefs.length < minSelectable ||
            selectedByDefaultQuantity < minAggregateQuantity) {
            modGroupReason = `${modGroupName}: default mod quantities do not meet required minimums`;
            return true;
        }
        const mods = await getMods(selectedByDefaultModRefs.map(({ _id }) => _id));
        await async.findSeries(mods, async (mod) => {
            const eligibility = await checkQuickAddEligibility(mod, getModGroups, getMods);
            if (!eligibility.itemIsQuickAddable) {
                modGroupReason = eligibility.reason;
                return true;
            }
            return false;
        });
    });
    if (modGroupReason) {
        return { itemIsQuickAddable: false, reason: modGroupReason };
    }
    return { itemIsQuickAddable: true };
}
//# sourceMappingURL=quick_add_rules.js.map